import { memo } from 'react';
import { isTextSelected } from '@/lib/is-text-selected';
import { isBuiltInReportSectionId } from '@/lib/report-sections';
import { useReportWizard } from '@/pages/create-report/context/ReportWizardContext';
import { TestBlock } from '@/types/report-payload';
import { AISectionSkeleton } from './AISectionSkeleton';
import { SectionCommentBubble } from './SectionCommentBubble';
import SectionContent from './SectionContent';
import SectionTitle from './SectionTitle';
import type { ReportFootnotePlan } from './footnotes';
import type { EditingTextSection, ReportData, Section } from './types';

interface ReportSectionProps {
    section: Section;
    data: ReportData;
    onUpdate: (updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>)) => void;
    disabled?: boolean;
    onSectionClick?: (sectionId: string) => void;
    isEditingSection?: boolean;
    /**
     * True on every fragment of the section being edited, unlike
     * `isEditingSection` which only marks the fragment that owns the editor.
     */
    isSectionEditing?: boolean;
    onEditingSectionChange?: (section: EditingTextSection) => void;
    renderKey?: string;
    onAddTest: (blockId?: string, mode?: 'default' | 'inline') => void;
    /**
     * Only the tests section's empty state uses it, for the "Instrumento Livre"
     * half of its menu — the Satepsi half stays on `onAddTest`. Optional so the
     * hidden measurer, which never renders that CTA, needs no no-op for it.
     */
    onAddBlock?: (toolId: string) => void;
    onAddChartFromTable: (blockId: string) => void;
    onEditChart: (block: TestBlock) => void;
    handleDeleteRequest: (sectionId: string) => void;
    idPrefix?: string;
    contentOverride?: string;
    blocksOverride?: TestBlock[];
    blockStartIndex?: number;
    editingBlockId?: string | number | null;
    onEditingBlockChange?: (blockId: string | number | null) => void;
    droppableKey?: string;
    commentCount?: number;
    onOpenAllComments?: () => void;
    footnotePlan?: ReportFootnotePlan;
}

type RenderableSectionState = Section & {
    isSplit?: boolean;
    hideTitle?: boolean;
};

function ReportSectionComponent({
    section,
    data,
    onUpdate,
    disabled,
    onSectionClick,
    isEditingSection,
    isSectionEditing,
    onEditingSectionChange,
    renderKey,
    onAddTest,
    onAddBlock,
    onAddChartFromTable,
    onEditChart,
    handleDeleteRequest,
    idPrefix = '',
    contentOverride,
    blocksOverride,
    blockStartIndex,
    editingBlockId,
    onEditingBlockChange,
    droppableKey,
    commentCount = 0,
    onOpenAllComments,
    footnotePlan,
}: ReportSectionProps) {
    const { generatingSectionId, generationPhase } = useReportWizard();
    const isGeneratingThis = section.id !== 'tests' && section.id === generatingSectionId;

    const renderableSection = section as RenderableSectionState;
    const isSplit = renderableSection.isSplit;
    const hideTitle = renderableSection.hideTitle;
    const shouldRenderId = !isSplit;

    return (
        <div
            id={shouldRenderId ? `${idPrefix}section-${section.id}` : undefined}
            className={`relative -m-2 mb-6 rounded-lg p-2 transition-colors ${
                disabled ? 'cursor-default' : 'cursor-pointer hover:bg-slate-50/50'
            }`}
            onClickCapture={(e) => {
                if (disabled || !onSectionClick) return;
                // Right-click (and its macOS Ctrl+click alias) belongs to the
                // section context menu, not to step navigation.
                if (e.button === 2 || e.ctrlKey) return;
                if (isTextSelected(e.currentTarget)) return;
                onSectionClick(section.id);
            }}
        >
            <SectionCommentBubble count={commentCount} onOpenAllComments={onOpenAllComments} />

            {!hideTitle && (
                <SectionTitle
                    section={section}
                    data={data}
                    onUpdate={onUpdate}
                    disabled={disabled}
                />
            )}

            {isGeneratingThis ? (
                <AISectionSkeleton visible sectionId={section.id} phase={generationPhase} />
            ) : (
                <SectionContent
                    section={section}
                    data={data}
                    onUpdate={onUpdate}
                    disabled={disabled}
                    onAddTest={onAddTest}
                    onAddBlock={onAddBlock}
                    onAddChartFromTable={onAddChartFromTable}
                    onEditChart={onEditChart}
                    handleDeleteRequest={handleDeleteRequest}
                    isEditingSection={isEditingSection}
                    isSectionEditing={isSectionEditing}
                    onEditingSectionChange={onEditingSectionChange}
                    renderKey={renderKey}
                    contentOverride={contentOverride}
                    blocksOverride={blocksOverride}
                    blockStartIndex={blockStartIndex}
                    editingBlockId={editingBlockId}
                    onEditingBlockChange={onEditingBlockChange}
                    droppableKey={droppableKey}
                    footnotePlan={footnotePlan}
                />
            )}
        </div>
    );
}

function areRelevantSectionSlicesEqual(
    prevData: ReportData,
    nextData: ReportData,
    sectionId: string,
) {
    if (sectionId === 'tests') {
        return prevData.tests === nextData.tests;
    }

    if (isBuiltInReportSectionId(sectionId)) {
        return prevData[sectionId as keyof ReportData] === nextData[sectionId as keyof ReportData];
    }

    return prevData.custom_sections === nextData.custom_sections;
}

function areEqualReportSectionProps(prevProps: ReportSectionProps, nextProps: ReportSectionProps) {
    return (
        prevProps.section.id === nextProps.section.id &&
        prevProps.section.title === nextProps.section.title &&
        prevProps.section.number === nextProps.section.number &&
        Boolean((prevProps.section as RenderableSectionState).isSplit) ===
            Boolean((nextProps.section as RenderableSectionState).isSplit) &&
        Boolean((prevProps.section as RenderableSectionState).hideTitle) ===
            Boolean((nextProps.section as RenderableSectionState).hideTitle) &&
        prevProps.disabled === nextProps.disabled &&
        prevProps.onSectionClick === nextProps.onSectionClick &&
        prevProps.isEditingSection === nextProps.isEditingSection &&
        prevProps.isSectionEditing === nextProps.isSectionEditing &&
        prevProps.onEditingSectionChange === nextProps.onEditingSectionChange &&
        prevProps.renderKey === nextProps.renderKey &&
        prevProps.onAddTest === nextProps.onAddTest &&
        prevProps.onAddBlock === nextProps.onAddBlock &&
        prevProps.onAddChartFromTable === nextProps.onAddChartFromTable &&
        prevProps.onEditChart === nextProps.onEditChart &&
        prevProps.handleDeleteRequest === nextProps.handleDeleteRequest &&
        prevProps.idPrefix === nextProps.idPrefix &&
        prevProps.contentOverride === nextProps.contentOverride &&
        prevProps.blocksOverride === nextProps.blocksOverride &&
        prevProps.blockStartIndex === nextProps.blockStartIndex &&
        prevProps.editingBlockId === nextProps.editingBlockId &&
        prevProps.onEditingBlockChange === nextProps.onEditingBlockChange &&
        prevProps.droppableKey === nextProps.droppableKey &&
        prevProps.commentCount === nextProps.commentCount &&
        prevProps.onOpenAllComments === nextProps.onOpenAllComments &&
        prevProps.footnotePlan === nextProps.footnotePlan &&
        areRelevantSectionSlicesEqual(prevProps.data, nextProps.data, prevProps.section.id)
    );
}

export const ReportSection = memo(ReportSectionComponent, areEqualReportSectionProps);
